/**
 * Region registry for the login-page region switcher (CNA-1902 spike / CNA-1906).
 *
 * Each region is a fully separate stack on its own subdomain — modelled on Datadog's
 * per-site domains (app.datadoghq.com, app.datadoghq.eu). The region a visitor is on is a
 * routing decision made *before* authentication, not a setting inside an authenticated
 * session, so this module only ever answers "which subdomain is region X" and "which
 * region is this host" — it carries no auth state and reaches no API.
 *
 * **A region exists only once its domain is configured.** There is deliberately no built-in
 * default domain: an unconfigured region is left out of the registry entirely, so it cannot
 * be offered. Raised by Duy on CNA-1906 — with a hardcoded default, shipping this before a
 * region's stack was reachable would offer a jump to a host that does not answer, and the
 * region cookie would then redirect that visitor there on every visit for the cookie's
 * lifetime, from a page that never loads. Opt-in makes that unreachable: a region is
 * offered because an environment named it, not because the code shipped.
 *
 * Nothing outside this file should ever hardcode a region domain, so changing or adding one
 * is a config edit, never a code change.
 *
 * **Read from runtime config, not `process.env`.** `process.env.REACT_APP_*` is inlined at
 * build time, and the UI image is built once and retagged per environment, so a build-time
 * value is necessarily the *same* in stage and prod — it cannot describe a per-environment
 * region map. Raised by Duy on CNA-1906. Runtime config comes from `runtime-env-cra`
 * (`public/runtime-env.js` → `window.__RUNTIME_CONFIG__`), the same source
 * `common-ui/src/observability/index.ts` reads `DD_RUM_*` from, with `process.env` kept only
 * as a build-time fallback for local `yarn start`.
 *
 * This is also what makes the switcher usable outside production. `RegionSwitcher` renders
 * nothing on a host that is not itself a region, so on `localhost` or `stage.cinareo.com` it
 * would be invisible and untestable — unless that environment's runtime config names its own
 * host as a region, which it now can. Setting `REGION_CA_DOMAIN=localhost:3000` locally makes
 * the switcher appear and keeps "Europe" pointing somewhere harmless instead of production EU.
 */

export type RegionCode = 'ca' | 'eu';

export interface RegionDefinition {
  code: RegionCode;
  /** i18n key for the region's display name in the switcher. */
  labelKey: string;
  /** Host only — no protocol, no path. */
  domain: string;
}

type RuntimeConfig = Record<string, string | undefined>;

const runtimeConfig = (): RuntimeConfig =>
  (globalThis as typeof globalThis & { __RUNTIME_CONFIG__?: RuntimeConfig }).__RUNTIME_CONFIG__ ?? {};

/**
 * Runtime config first, build-time env as a local-dev fallback. No production default:
 * an unconfigured region is not a region. A blank value counts as unset, so clearing the
 * variable is a usable way to switch a region off.
 */
const domainFor = (key: string, buildTime: string | undefined): string | undefined =>
  runtimeConfig()[key]?.trim() || buildTime?.trim() || undefined;

const REGION_CONFIG: { code: RegionCode; labelKey: string; configKey: string; buildTime: string | undefined }[] = [
  {
    code: 'ca',
    labelKey: 'login.region.ca',
    configKey: 'REGION_CA_DOMAIN',
    buildTime: process.env.REACT_APP_REGION_CA_DOMAIN,
  },
  {
    code: 'eu',
    labelKey: 'login.region.eu',
    configKey: 'REGION_EU_DOMAIN',
    buildTime: process.env.REACT_APP_REGION_EU_DOMAIN,
  },
];

/**
 * The regions this environment has been configured to know about — those with a domain, in
 * declaration order. Built per call rather than frozen in a module-level const: runtime
 * config is only on `window` once `runtime-env.js` has run, and a const captured at import
 * time would also make the value impossible to vary between tests.
 */
export const getRegions = (): RegionDefinition[] =>
  REGION_CONFIG.flatMap(({ code, labelKey, configKey, buildTime }) => {
    const domain = domainFor(configKey, buildTime);
    return domain ? [{ code, labelKey, domain }] : [];
  });

/** Which registered region, if any, a host (e.g. `window.location.host`) belongs to. */
export const regionForHost = (host: string): RegionDefinition | undefined =>
  getRegions().find((region) => host === region.domain || host.startsWith(`${region.domain}:`));

/**
 * The configured region with this code, or undefined when this environment does not know
 * it. Undefined rather than a fallback: the only caller is the remembered-region redirect,
 * and sending someone to a region they did not choose because their own is unconfigured is
 * worse than doing nothing.
 */
export const regionByCode = (code: RegionCode): RegionDefinition | undefined =>
  getRegions().find((region) => region.code === code);

type LocationParts = Pick<Location, 'protocol' | 'pathname' | 'search' | 'hash'>;

/**
 * Where to send the browser to land on `region`, preserving the current path, query and
 * hash. Pure string construction — callers decide if/when to actually navigate, so this
 * stays trivially testable without mocking `window.location`.
 */
export const regionSwitchUrl = (region: RegionDefinition, current: LocationParts): string =>
  `${current.protocol}//${region.domain}${current.pathname}${current.search}${current.hash}`;
